import React, { useEffect, useRef, useState } from 'react';
import './MessageToasts.css';

const VISIBLE_MS = 4200;
const EXIT_MS    = 440;

/**
 * MessageToasts — single rolling notification, glass-morphism styled.
 *
 * Placement:
 *   - Desktop → top corner on the CHAT side (= opposite the video tiles, which
 *               anchor opposite the chat), so the two never overlap:
 *                 chatPosition 'Left'  → toast top-left,  tiles top-right
 *                 chatPosition 'Right' → toast top-right, tiles top-left
 *   - Mobile  → bottom corner on the same side as the controlPanel chat icon,
 *               smaller, sliding in from that edge. The controlPanel sits on
 *               the OPPOSITE side of `position` (chatPosition):
 *                 chatPosition 'Left'  → controlPanel right → toast bottom-right
 *                 chatPosition 'Right' → controlPanel left  → toast bottom-left
 *
 * Behaviour: new messages replace the toast content + reset the timer (no
 * stacking). Click invokes onClick (opens the panel) and dismisses.
 *
 * Props: messages, selfKey, active, onClick, darkMode, isMobile, position
 */
export default function MessageToasts({
  messages = [],
  selfKey = 'You',
  active = false,
  onClick,
  darkMode = true,
  isMobile = false,
  position = 'Right',   // chatPosition
}) {
  const seenCount = useRef(0);
  const [toast, setToast] = useState(null);  // { id, from, text, more, exiting } | null

  // Desktop: anchor the toast on the chat side (= opposite the video tiles,
  // which sit opposite the chat), so the two never overlap when text + video
  // chat are both on. Mobile: opposite side (near the controlPanel chat icon).
  const placement = isMobile
    ? (position === 'Left' ? 'mobile-right' : 'mobile-left')
    : (position === 'Left' ? 'desktop-left' : 'desktop-right');

  useEffect(() => {
    if (!active) {
      setToast(null);
      seenCount.current = messages.length;
    }
  }, [active, messages.length]);

  useEffect(() => {
    if (!active) { seenCount.current = messages.length; return; }
    if (messages.length <= seenCount.current) return;
    const fresh = messages
      .slice(seenCount.current)
      .filter(m => m.from && m.from !== selfKey);
    seenCount.current = messages.length;
    if (fresh.length === 0) return;

    const latest = fresh[fresh.length - 1];
    setToast(prev => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      from: (latest.from || '').split('&')[0],
      text: latest.text || '',
      more: (prev?.more || 0) + (fresh.length - 1),
      exiting: false,
    }));
  }, [messages, active, selfKey]);

  useEffect(() => {
    if (!toast || toast.exiting) return;
    const exitTimer = setTimeout(() => {
      setToast(prev => prev ? { ...prev, exiting: true } : null);
    }, VISIBLE_MS);
    const removeTimer = setTimeout(() => setToast(null), VISIBLE_MS + EXIT_MS);
    return () => { clearTimeout(exitTimer); clearTimeout(removeTimer); };
  }, [toast?.id, toast?.exiting]);

  const dismiss = () => {
    setToast(prev => prev ? { ...prev, exiting: true } : null);
    setTimeout(() => setToast(null), EXIT_MS);
  };

  if (!active || !toast) return null;

  return (
    <div
      className={`msg-toasts msg-toasts-${darkMode ? 'dark' : 'light'} msg-toasts-${placement}`}
      aria-live="polite"
      aria-atomic="true"
    >
      <button
        className={`msg-toast ${toast.exiting ? 'is-exiting' : ''}`}
        onClick={() => { onClick?.(); dismiss(); }}
      >
        {/* keyed inner → replays the update fade when content changes */}
        <span className="msg-toast-content">
          <span className="msg-toast-avatar" aria-hidden="true">
            {(toast.from?.[0] || '?').toUpperCase()}
          </span>
          <span className="msg-toast-body">
            <span className="msg-toast-from">
              {toast.from}
              {toast.more > 0 && <span className="msg-toast-more"> · +{toast.more}</span>}
            </span>
            <span className="msg-toast-text">{toast.text}</span>
          </span>
        </span>
      </button>
    </div>
  );
}
